import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { useAdminMember, useAdminLoans, useUpdateMemberKyc, useUpdateMemberStatus, useKyc } from "../../hooks/useAdmin";
import Card from "../../components/ui/Card";
import Badge from "../../components/ui/Badge";
import Button from "../../components/ui/Button";
import Avatar from "../../components/ui/Avatar";
import StatCard from "../../components/ui/StatCard";

const fmt = (n:number) => new Intl.NumberFormat("en-NG",{style:"currency",currency:"NGN", currencyDisplay:"symbol",maximumFractionDigits:0}).format(n);
const fmtDate = (d:string) => d ? new Date(d).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}) : "—";

export default function MemberDetailPage() {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const { id }   = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const { data: m }              = useAdminMember(id!)    as any;
  const { data: loans = [] }     = useAdminLoans()        as any;
  const {data: kyc} = useKyc(id!) as any;

  const updateKyc    = useUpdateMemberKyc();
  const updateStatus = useUpdateMemberStatus();

  const [docPath, setDocPath] = useState("");
  const [reason, setReason] = useState("");

  const openForm = () => {
    dialogRef.current?.showModal() 
  };
  const closeForm = () => { dialogRef.current?.close() };
  const closeBtnStyle = { background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer' };
  const dialogStyle = {padding: '20px', borderRadius: '12px', border: '1px solid #241917', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', width: '400px', top: '50px', left: '30px', };
  
  if (!m) return <div className="p-8 text-center text-slate-400">Loading…</div>;

  const memberLoans = loans.filter((l: any) => l.borrower_id === id);
  const kycColor  = (k:string) => k==="verified"?"emerald":k==="reviewing"?"amber":"red";
  const statColor = (s:string) => s==="active"?"emerald":s==="suspended"?"red":"amber";

  const baseUrl = import.meta.env.VITE_BASE_URL;
  let imgUrl = "";
  imgUrl = baseUrl+m.first_name+m.member_no+'.jpg';

  return (
    <div>
      <Button variant="outline" size="sm" onClick={() => navigate("/admin/members")} className="mb-5">← Back</Button>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-4">
        {/* Profile */}
        <Card className="lg:col-span-2">
          <div className="flex gap-4 items-center mb-5">
            <Avatar initials={`${m.first_name?.[0]}${m.last_name?.[0]}`} size={56}/>
            <div>
              <h2 className="text-lg font-medium text-slate-800">{m.first_name} {m.last_name}</h2>
              <p className="text-sm text-slate-500 mb-1.5">{m.email} | {m.phone}</p>
              <Badge label={m.status} color={statColor(m.status) as any}/>
            </div>
          </div>
          {[
            {label:"Member since", value:fmtDate(m.created_at)},
            {label:"KYC status",   value:m.kyc_status},
            {label:"Total Sum",      value:fmt(m.balance||0)},
            {label:"One-off Charge", value:fmt(m.charges || 0)},
            {label:"Balance",      value:fmt((m.balance - m.charges) ||0)},
            {label:"Locked",       value:fmt(m.locked_amount||0)},
          ].map(r => (
            <div key={r.label} className="flex justify-between py-2.5 border-t border-slate-100">
              <span className="text-sm text-slate-500">{r.label}</span>
              <span className="text-sm font-medium text-slate-800">{r.value}</span>
            </div>
          ))}
          {kyc?.id==null? <p>KYC Document ...</p> : (                      
              <div className="flex justify-between py-2.5 border-t border-slate-100">
                <span className="text-sm text-slate-500">{kyc.doc_type.toUpperCase().replace('_',' ')}</span>
                <Button size="sm" variant="amber"  onClick={() => setDocPath(kyc.file_path)}>View Document</Button>
              </div>
          )}
        </Card>

        {/* Actions */}
        <div className="lg:col-span-3 space-y-4">
          <Card>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <p className="text-sm font-medium text-slate-700 mb-3">KYC management</p>
                <div className="flex items-center gap-2 mb-3"><Badge label={`KYC: ${m.kyc_status}`} color={kycColor(m.kyc_status) as any}/></div>
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" onClick={() => updateKyc.mutate({id,kyc_status:"verified",reason:""})} disabled={m.kyc_status==="verified"}>✅ Approve KYC</Button>
                  <Button size="sm" variant="danger" onClick={openForm} disabled={m.kyc_status==="rejected"}>Reject</Button>
                  <Button size="sm" variant="amber"  onClick={() => updateKyc.mutate({id,kyc_status:"reviewing",reason:""})}>Flag for Review</Button>
                </div>
              </div>
              <img src={imgUrl? imgUrl : ""} alt="Member photo" style={{ maxWidth: '100%', height: 'auto', borderRadius:'8px'}} />
            </div>
            <dialog ref={dialogRef} style={dialogStyle} >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom:'15px' }}>
                <h3><span className="text-xl">🚫📜</span> Document Reject Reason</h3>
                <button type="button" onClick={closeForm} style={closeBtnStyle}>✕</button>
              </div>
                <input value={reason} className="w-full px-3 py-2.5 border rounded-lg text-sm text-slate-800 outline-none bg-white" onChange={(e) => setReason(e.target.value)} placeholder="Enter reject reason" maxLength={255} required style={{marginBottom:"10px"}} />
                <Button variant="danger" size="sm" onClick={() => {updateKyc.mutate({id,kyc_status:"rejected",reason:reason}); closeForm()}}>
                  Reject KYC Document
                </Button>
            </dialog>
          </Card>
          <Card>
            <p className="text-sm font-medium text-slate-700 mb-3">Account management</p>
            <div className="flex gap-2 flex-wrap">
              {m.status==="active"    && <Button size="sm" variant="outline"   onClick={() => updateStatus.mutate({id,status:"suspended"})}>⛔ Suspend</Button>}
              {m.status==="suspended" && <Button size="sm"                     onClick={() => updateStatus.mutate({id,status:"active"})}>✅ Reinstate</Button>}
              <Button size="sm" variant="secondary">📧 Send Notice</Button>
              <Button size="sm" variant="secondary">🔒 Reset Password</Button>
            </div>
          </Card>
          <div className="grid grid-cols-2 gap-3">
            <StatCard label="Credit score" value={m.credit_score||700} sub="Excellent" color="text-emerald-600"/>
            <StatCard label="Loans"        value={memberLoans.length}   sub="Total"    color="text-blue-600"/>
          </div>
        </div>
      </div>

      {/* Loan history */}
      {memberLoans.length > 0 && (
        <Card>
          <p className="text-sm font-medium text-slate-700 mb-4">Loan history</p>
          {memberLoans.map((l:any) => (
            <div key={l.id} className="py-3 border-b border-slate-100 last:border-0">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <p className="text-sm font-medium text-slate-800">{fmt(l.amount)} — {l.purpose}</p>
                  <p className="text-xs text-slate-400">#{l.id} · {fmtDate(l.disbursed_at)}</p>
                </div>
                <Badge label={l.status} color={(l.status==="active"?"emerald":l.status==="repaid"?"blue":l.status==="overdue"?"red":"amber") as any}/>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full" style={{width:`${Math.round((l.amount_repaid/l.amount)*100)}%`}}/>
              </div>
              <p className="text-xs text-slate-400 mt-1">Repaid {fmt(l.amount_repaid)} of {fmt(l.amount)}</p>
            </div>
          ))}
        </Card>
      )}

      <div className="lg:col-span-3 space-y-4">
        <Card>          
            {docPath == ""? "" : (
              <div style={{ width: "100%", height: "80vh" }}>
                <iframe
                  src={docPath}
                  width="100%"
                  height="100%"
                  title="PDF Viewer"
                  style={{ border: "none" }}
                />
            </div>
            ) }        
        </Card>
      </div>

    </div>
  );
}
